import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Wallet, Building2, Smartphone, Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const fmt = (c) => `$${(c / 100).toFixed(2)}`;

const PROVIDER_META = {
  stripe: { label: "Credit / Debit Card", icon: CreditCard, kind: "online" },
  square: { label: "Square (Card / Apple Pay / Google Pay / Cash App)", icon: Wallet, kind: "online" },
  paypal: { label: "PayPal / Venmo", icon: Wallet, kind: "online" },
  zelle: { label: "Zelle", icon: Building2, kind: "manual" },
  chime: { label: "Chime", icon: Building2, kind: "manual" },
  cash_app: { label: "Cash App", icon: Smartphone, kind: "manual" },
  venmo: { label: "Venmo", icon: Smartphone, kind: "manual" },
  apple_pay: { label: "Apple Pay (manual)", icon: Smartphone, kind: "manual" },
  google_pay: { label: "Google Pay (manual)", icon: Smartphone, kind: "manual" },
};

export default function Checkout() {
  const navigate = useNavigate();
  const { items, totalCents, clear } = useCart();
  const [providers, setProviders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "", shipping_address: "",
  });

  useEffect(() => {
    api.get("/settings/public").then((r) => {
      const ps = r.data.providers || [];
      setProviders(ps);
      if (ps.length) setSelected(ps[0].provider_type);
    }).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const placeOrder = async (provider, payment_metadata = {}) => {
    if (!form.customer_name || !form.customer_email || !form.customer_phone || !form.shipping_address) {
      toast.error("Please fill in your contact and shipping details"); return;
    }
    if (items.length === 0) { toast.error("Your cart is empty"); return; }
    const isManual = ["zelle", "chime", "cash_app", "venmo", "apple_pay", "google_pay"].includes(provider);
    const payment_provider = isManual ? `manual_${provider}` : provider;
    setSubmitting(true);
    try {
      const r = await api.post("/orders", {
        ...form, items, total_cents: totalCents,
        payment_provider, payment_metadata,
      });
      toast.success("Order placed!");
      clear();
      navigate(`/order-success?id=${r.data.id}&status=${r.data.status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Order failed");
    } finally { setSubmitting(false); }
  };

  const selectedProvider = providers.find((p) => p.provider_type === selected);

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-24 text-center md:px-8">
        <h1 className="font-display text-3xl font-bold text-slate-900">Your cart is empty</h1>
        <p className="mt-2 text-slate-500">Pop into the shop and add a few favourites.</p>
        <Button onClick={() => navigate("/shop")} className="btn-aqua mt-6" data-testid="checkout-go-to-shop-button">Go to shop</Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-5 py-12 md:px-8 md:py-16">
      <h1 className="font-display text-4xl font-bold text-slate-900 wavy">Checkout</h1>
      <div className="mt-8 grid gap-8 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-7">
          <section className="rounded-2xl border border-cyan-100 bg-white p-6 md:p-8">
            <h2 className="font-display text-xl font-bold">Contact & shipping</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div><Label>Name</Label><Input className="mt-1" value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} data-testid="checkout-name-input" /></div>
              <div><Label>Email</Label><Input className="mt-1" type="email" value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} data-testid="checkout-email-input" /></div>
              <div><Label>Phone</Label><Input className="mt-1" value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} data-testid="checkout-phone-input" /></div>
              <div><Label>Shipping address</Label><Input className="mt-1" value={form.shipping_address} onChange={(e) => update("shipping_address", e.target.value)} data-testid="checkout-address-input" /></div>
            </div>
          </section>

          <section className="rounded-2xl border border-cyan-100 bg-white p-6 md:p-8">
            <h2 className="font-display text-xl font-bold">Payment method</h2>
            {providers.length === 0 && (
              <div className="mt-4 rounded-xl bg-amber-50 p-4 text-sm text-amber-800">
                The owner hasn't enabled any payment methods yet. Please check back shortly.
              </div>
            )}
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {providers.map((p) => {
                const meta = PROVIDER_META[p.provider_type] || { label: p.provider_type, icon: CreditCard };
                const Icon = meta.icon;
                const active = selected === p.provider_type;
                return (
                  <button
                    key={p.provider_type}
                    type="button"
                    onClick={() => setSelected(p.provider_type)}
                    className={`flex items-center gap-3 rounded-xl border p-4 text-left transition ${active ? "border-cyan-500 bg-cyan-50" : "border-slate-200 bg-white hover:border-cyan-300"}`}
                    data-testid={`checkout-provider-${p.provider_type}-button`}
                  >
                    <Icon className={`h-5 w-5 ${active ? "text-cyan-700" : "text-slate-500"}`} />
                    <div>
                      <div className="font-medium text-slate-900">{p.display_name || meta.label}</div>
                      <div className="text-xs text-slate-500">{meta.kind === "manual" ? "Pay manually & confirm" : "Pay online securely"}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {selectedProvider && (
              <div className="mt-6 rounded-xl border border-cyan-100 bg-cyan-50/40 p-5">
                {(() => {
                  const t = selectedProvider.provider_type;
                  if (t === "stripe") {
                    return (
                      <div>
                        <div className="text-sm text-slate-700">You'll be charged <strong>{fmt(totalCents)}</strong> securely via Stripe. (Demo: order is placed; real card form is collected when Stripe is fully configured.)</div>
                        <Button onClick={() => placeOrder("stripe", { simulated: true })} disabled={submitting} className="btn-aqua mt-4" data-testid="checkout-pay-stripe-button">
                          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Pay with Stripe
                        </Button>
                      </div>
                    );
                  }
                  if (t === "square") {
                    return (
                      <div>
                        <div className="text-sm text-slate-700">Square accepts Card, Apple Pay, Google Pay and Cash App Pay. You'll see those options once Square keys are set in the dashboard.</div>
                        <Button onClick={() => placeOrder("square", { simulated: true })} disabled={submitting} className="btn-aqua mt-4" data-testid="checkout-pay-square-button">
                          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Pay with Square
                        </Button>
                      </div>
                    );
                  }
                  if (t === "paypal") {
                    return (
                      <div>
                        <div className="text-sm text-slate-700">PayPal also lets your customers check out with Venmo where supported.</div>
                        <Button onClick={() => placeOrder("paypal", { simulated: true })} disabled={submitting} className="btn-aqua mt-4" data-testid="checkout-pay-paypal-button">
                          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Pay with PayPal
                        </Button>
                      </div>
                    );
                  }
                  // Manual
                  return (
                    <ManualInstructions provider={selectedProvider} totalCents={totalCents} onConfirm={(meta) => placeOrder(t, meta)} submitting={submitting} />
                  );
                })()}
              </div>
            )}
          </section>
        </div>

        <aside className="lg:col-span-5">
          <div className="sticky top-24 rounded-2xl border border-cyan-100 bg-white p-6 md:p-8">
            <h2 className="font-display text-xl font-bold">Order summary</h2>
            <ul className="mt-4 divide-y divide-slate-100">
              {items.map((i) => (
                <li key={i.product_id} className="flex justify-between gap-4 py-3 text-sm">
                  <div className="text-slate-700">{i.quantity}× {i.name}</div>
                  <div className="font-medium text-slate-900">{fmt(i.price_cents * i.quantity)}</div>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-4">
              <span className="text-sm text-slate-500">Total</span>
              <span className="font-display text-2xl font-bold text-slate-900" data-testid="checkout-total">{fmt(totalCents)}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function ManualInstructions({ provider, totalCents, onConfirm, submitting }) {
  const [confirmed, setConfirmed] = useState(false);
  const [reference, setReference] = useState("");
  const fmtV = `$${(totalCents / 100).toFixed(2)}`;
  const handle = provider.handle || "(handle not set)";
  const customInstructions = provider.instructions;

  return (
    <div>
      <div className="rounded-lg bg-white p-4 ring-1 ring-cyan-100">
        <div className="text-sm text-slate-500">Send</div>
        <div className="font-display text-2xl font-bold text-slate-900">{fmtV}</div>
        <div className="mt-2 text-sm text-slate-600">to <span className="font-mono text-cyan-700">{handle}</span> via <strong>{provider.display_name || provider.provider_type}</strong></div>
        {customInstructions && <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600">{customInstructions}</p>}
      </div>
      <div className="mt-4">
        <Label>Reference / your name on the transfer (optional)</Label>
        <Input className="mt-1" value={reference} onChange={(e) => setReference(e.target.value)} data-testid="checkout-manual-reference-input" />
      </div>
      <label className="mt-3 flex items-start gap-2 text-sm">
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-1" data-testid="checkout-manual-confirm-checkbox" />
        I have sent {fmtV} via {provider.display_name || provider.provider_type}.
      </label>
      <Button
        onClick={() => onConfirm({ handle, reference })}
        disabled={!confirmed || submitting}
        className="btn-aqua mt-4"
        data-testid="checkout-manual-submit-button"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        Submit order (pending verification)
      </Button>
    </div>
  );
}
